import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import MathRenderer from '../math/MathRenderer';
import WorkedSolutionPanel from './WorkedSolutionPanel';
import SimilarQuestionPanel from './SimilarQuestionPanel';
import {
  QuestionProgress,
  QuestionTag,
  ShortAnswerQuestionGroup,
  ShortAnswerSubQuestion,
} from '../../types/questions';
import { AttemptResult } from '../../types/practice';
import { formatRating, formatRatingChange } from '../../utils/format';
import { questionAPI } from '../../services/api';
import { getUserMessage } from '../../utils/errors';

interface ShortAnswerQuestionCardProps {
  question: ShortAnswerQuestionGroup;
  onSubmitAnswer: (params: {
    questionId: string;
    selectedAnswer: string;
    autoSubmitted?: boolean;
    timeSpentSeconds?: number;
  }) => Promise<AttemptResult>;
  onNextQuestion?: () => void;
  practiceLimitReached?: boolean;
  tags?: QuestionTag[];
  showSimilarQuestion?: boolean;
  canGenerateSimilar?: boolean;
  showTimer?: boolean;
  onBookmarkChange?: (questionId: string, isBookmarked: boolean) => void;
}

interface SubQuestionPanelProps {
  subQuestion: ShortAnswerSubQuestion;
  tags?: QuestionTag[];
  areaOfStudy: string;
  progress?: QuestionProgress | null;
  disabled?: boolean;
  onSubmit: (selectedAnswer: string, autoSubmitted?: boolean, timeSpentSeconds?: number) => Promise<AttemptResult>;
  onAttemptComplete: (result: AttemptResult) => void;
  showTimer?: boolean;
  timerEnabled?: boolean;
  autoStartTimer?: boolean;
  onBookmarkChange?: (questionId: string, isBookmarked: boolean) => void;
}

const ANSWER_TIME_LIMIT = 30; // seconds

const shuffleOptions = (options: ShortAnswerSubQuestion['options']) => {
  const result = [...options];
  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
};

const formatTimer = (value: number) => {
  const minutes = Math.floor(value / 60);
  const seconds = value % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

const formatProgressDate = (value?: string | null) => {
  if (!value) return '';
  try {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return '';
    }
    return parsed.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '';
  }
};

const getDifficultyLabel = (rating: number | null) => {
  if (rating === null || rating === undefined) return 'Unknown';
  if (rating < 25) return 'Easy';
  if (rating < 35) return 'Medium';
  return 'Hard';
};

const getDifficultyColor = (rating: number | null) => {
  if (rating === null || rating === undefined) return 'text-gray-500 dark:text-gray-400';
  if (rating < 25) return 'text-green-600 dark:text-green-400';
  if (rating < 35) return 'text-yellow-600 dark:text-yellow-400';
  return 'text-red-600 dark:text-red-400';
};

const getProgressStatus = (progress?: QuestionProgress | null) => {
  if (!progress || !progress.last_attempt_at || progress.last_attempt_was_correct === null || progress.last_attempt_was_correct === undefined) {
    return {
      label: 'New',
      className: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
    };
  }

  const formatted = formatProgressDate(progress.last_attempt_at);

  if (progress.last_attempt_was_correct) {
    return {
      label: formatted ? `Last Solved Correctly on ${formatted}` : 'Last Solved Correctly',
      className: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
    };
  }

  return {
    label: formatted ? `Last Solved Incorrectly on ${formatted}` : 'Last Solved Incorrectly',
    className: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
  };
};

const RatingChange: React.FC<{ result: AttemptResult }> = ({ result }) => (
  <div className="mt-3 p-3 rounded-lg bg-white dark:bg-gray-600">
    <div className="mb-3">
      <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        Your Rating
      </div>
      <div className="flex items-center justify-center space-x-2 mb-3">
        <span className="text-lg font-bold text-gray-700 dark:text-gray-300">
          {formatRating(result.rating_before)}
        </span>
        <span className="text-gray-400">→</span>
        <span className="text-lg font-bold text-gray-900 dark:text-white">
          {formatRating(result.rating_after)}
        </span>
        <span
          className={`text-sm font-semibold ${
            result.rating_change > 0
              ? 'text-green-600 dark:text-green-400'
              : result.rating_change < 0
              ? 'text-blue-600 dark:text-blue-400'
              : 'text-gray-600 dark:text-gray-400'
          }`}
        >
          ({formatRatingChange(result.rating_change)})
        </span>
      </div>
      {result.rating_bonus !== undefined && result.rating_bonus > 0 && (
        <div className="flex justify-center mb-2">
          <span className="inline-flex items-center rounded-full bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-200 px-3 py-1 text-xs font-semibold">
            Speed Bonus {formatRatingChange(result.rating_bonus)}
          </span>
        </div>
      )}
    </div>
  </div>
);

const SubQuestionPanel: React.FC<SubQuestionPanelProps> = ({
  subQuestion,
  tags,
  areaOfStudy,
  progress,
  disabled = false,
  onSubmit,
  onAttemptComplete,
  showTimer = false,
  timerEnabled = false,
  autoStartTimer = false,
  onBookmarkChange,
}) => {
  const [revealed, setRevealed] = useState(false);
  const [shuffledOptions, setShuffledOptions] = useState(subQuestion.options);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [attemptResult, setAttemptResult] = useState<AttemptResult | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState<number>(ANSWER_TIME_LIMIT);
  const [timerActive, setTimerActive] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [isBookmarked, setIsBookmarked] = useState(Boolean(subQuestion.is_bookmarked));
  const [isBookmarkUpdating, setIsBookmarkUpdating] = useState(false);
  const [bookmarkError, setBookmarkError] = useState<string | null>(null);
  const timerStartRef = useRef<number | null>(null);

  useEffect(() => {
    setRevealed(false);
    setShuffledOptions(subQuestion.options);
    setSelectedOption(null);
    setIsSubmitting(false);
    setAttemptResult(null);
    setIsSubmitted(false);
    setRemainingSeconds(ANSWER_TIME_LIMIT);
    setTimerActive(false);
    setErrorMessage(null);
    timerStartRef.current = null;
    setElapsedSeconds(0);
    setBookmarkError(null);
  }, [subQuestion.id, subQuestion.options, showTimer]);

  useEffect(() => {
    if (!showTimer || !timerEnabled || timerStartRef.current === null || isSubmitted) {
      return;
    }
    const intervalId = window.setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - (timerStartRef.current as number)) / 1000));
    }, 1000);
    return () => window.clearInterval(intervalId);
  }, [showTimer, timerEnabled, isSubmitted, subQuestion.id]);

  useEffect(() => {
    if (!timerActive || isSubmitted) {
      return undefined;
    }

    if (remainingSeconds <= 0) {
      const autoSubmit = async () => {
        setTimerActive(false);
        setIsSubmitting(true);
        try {
          const timeSpentSeconds = showTimer && timerStartRef.current
            ? Math.floor((Date.now() - timerStartRef.current) / 1000)
            : undefined;
          const result = await onSubmit('Z', true, timeSpentSeconds);
          setAttemptResult(result);
          setIsSubmitted(true);
          onAttemptComplete(result);
        } catch (error) {
          console.error('Failed to auto-submit timeout', error);
          setErrorMessage(getUserMessage(error, 'Unable to submit answer.'));
        } finally {
          setIsSubmitting(false);
        }
      };
      autoSubmit();
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setRemainingSeconds((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => window.clearTimeout(timeoutId);
  }, [timerActive, remainingSeconds, isSubmitted, onSubmit, onAttemptComplete, showTimer]);

  const startTimerAndReveal = () => {
    setRevealed(true);
    setShuffledOptions(shuffleOptions(subQuestion.options));
    setTimerActive(true);
    setRemainingSeconds(ANSWER_TIME_LIMIT);
    setErrorMessage(null);

    if (showTimer && timerStartRef.current === null) {
      timerStartRef.current = Date.now();
      setElapsedSeconds(0);
    }
  };

  const handleReveal = () => {
    if (disabled || revealed) return;
    if (showTimer && !timerEnabled) return;
    startTimerAndReveal();
  };

  const handleStartTimer = () => {
    if (disabled || revealed || !timerEnabled) return;
    startTimerAndReveal();
  };

  const manualTimerStartRequired = showTimer && !autoStartTimer;

  const renderInitialAction = () => {
    if (manualTimerStartRequired) {
      return (
        <button
          onClick={handleStartTimer}
          disabled={disabled || !timerEnabled}
          className="px-4 py-2 bg-subject-600 text-white rounded-lg hover:bg-subject-500 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
        >
          Start timer
        </button>
      );
    }

    return (
      <button
        onClick={handleReveal}
        disabled={disabled || (showTimer && !timerEnabled)}
        className="px-4 py-2 bg-subject-600 text-white rounded-lg hover:bg-subject-500 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
      >
        Reveal Answer Choices
      </button>
    );
  };

  const handleSelect = (optionKey: string) => {
    if (!revealed || isSubmitted || isSubmitting) return;
    setSelectedOption(optionKey);
  };

  const handleSubmit = async () => {
    if (!revealed || isSubmitted || isSubmitting || !timerActive) return;
    if (!selectedOption) return;

    setIsSubmitting(true);
    try {
      const timeSpentSeconds = showTimer && timerStartRef.current
        ? Math.floor((Date.now() - timerStartRef.current) / 1000)
        : undefined;
      setElapsedSeconds(timeSpentSeconds ?? elapsedSeconds);
      const result = await onSubmit(selectedOption, false, timeSpentSeconds);
      setAttemptResult(result);
      setIsSubmitted(true);
      setTimerActive(false);
      onAttemptComplete(result);
    } catch (error) {
      console.error('Failed to submit written response', error);
      setErrorMessage(getUserMessage(error, 'Failed to submit answer.'));
      setTimerActive(false);
    } finally {
      setIsSubmitting(false);
    }
  };


  useEffect(() => {
    setIsBookmarked(Boolean(subQuestion.is_bookmarked));
    setBookmarkError(null);
  }, [subQuestion.id, subQuestion.is_bookmarked]);

  const handleBookmarkToggle = async () => {
    if (isBookmarkUpdating) return;

    const nextValue = !isBookmarked;
    setIsBookmarked(nextValue);
    setIsBookmarkUpdating(true);
    setBookmarkError(null);
    onBookmarkChange?.(subQuestion.id, nextValue);

    try {
      const response = nextValue
        ? await questionAPI.bookmarkQuestion(subQuestion.id)
        : await questionAPI.unbookmarkQuestion(subQuestion.id);
      setIsBookmarked(response.is_bookmarked);
      if (response.is_bookmarked !== nextValue) {
        onBookmarkChange?.(subQuestion.id, response.is_bookmarked);
      }
    } catch (error) {
      console.error('Failed to update bookmark', error);
      setIsBookmarked(!nextValue);
      onBookmarkChange?.(subQuestion.id, !nextValue);
      setBookmarkError('Could not update bookmark. Please try again.');
    } finally {
      setIsBookmarkUpdating(false);
    }
  };

  const status = getProgressStatus(progress);
  const marksValue = subQuestion.marks ?? null;
  const marksLabel =
    marksValue && marksValue > 0 ? `${marksValue} mark${marksValue === 1 ? '' : 's'}` : null;
  const timerMarksValue = marksValue && marksValue > 0 ? marksValue : 1;
  const idealTimeSeconds = Math.round(timerMarksValue * 90);
  const difficultyLabel =
    subQuestion.difficulty_rating === null || subQuestion.difficulty_rating === undefined
      ? 'Unknown'
      : `${getDifficultyLabel(subQuestion.difficulty_rating)} (${subQuestion.difficulty_rating})`;

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-5 bg-white dark:bg-gray-800 shadow-sm">
      <div className="flex justify-between items-start mb-3">
        <div>
          <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">
            Sub-question {subQuestion.sub_question === 'N/A' ? 'Main' : subQuestion.sub_question}
            {marksLabel && <span className="ml-2 text-xs text-gray-400">({marksLabel})</span>}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Difficulty:{' '}
            <span className={getDifficultyColor(subQuestion.difficulty_rating)}>
              {difficultyLabel}
            </span>
          </div>
          {showTimer && (
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Time:{' '}
              <span className={`font-semibold ${elapsedSeconds > idealTimeSeconds ? 'text-red-600 dark:text-red-400' : 'text-gray-700 dark:text-gray-300'}`}>
                {formatTimer(elapsedSeconds)}
              </span>
              <span className="text-gray-400 dark:text-gray-500">
                {' '} / {formatTimer(idealTimeSeconds)} ideal
              </span>
            </div>
          )}
          <div className="text-gray-900 dark:text-white text-base">
            <MathRenderer latex={subQuestion.question_text} />
          </div>
        </div>
        <div className="flex items-start gap-2">
          <span className={`px-2 py-1 text-xs font-semibold rounded-full whitespace-nowrap ${status.className}`}>
            {status.label}
          </span>
          <button
            type="button"
            onClick={handleBookmarkToggle}
            disabled={isBookmarkUpdating}
            aria-pressed={isBookmarked}
            aria-label={isBookmarked ? 'Remove bookmark' : 'Bookmark sub-question'}
            title={isBookmarked ? 'Remove bookmark' : 'Bookmark sub-question'}
            className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs font-semibold transition-colors disabled:opacity-60 ${
              isBookmarked
                ? 'border-subject-500 bg-subject-50 text-subject-700 dark:bg-subject-900/30 dark:text-subject-200'
                : 'border-gray-300 bg-white text-gray-600 hover:border-subject-400 hover:text-subject-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:text-subject-200'
            }`}
          >
            <span aria-hidden="true">{isBookmarked ? '🔖' : '♡'}</span>
            <span>{isBookmarked ? 'Bookmarked' : 'Bookmark'}</span>
          </button>
        </div>
      </div>

      {bookmarkError && (
        <div className="mb-3 px-3 py-2 rounded-lg bg-red-50 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-200">
          {bookmarkError}
        </div>
      )}

      {!revealed && renderInitialAction()}

      {revealed && !isSubmitted && (
        <div className="flex items-center justify-between bg-gray-100 dark:bg-gray-700 px-4 py-2 rounded-lg mt-3">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Time Remaining
          </span>
          <span className={`text-lg font-semibold ${remainingSeconds <= 5 ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>
            {remainingSeconds}s
          </span>
        </div>
      )}

      {revealed && (
        <div className="space-y-3 mt-4">
          {shuffledOptions.map((option) => {
            const isCorrect = attemptResult && option.key === subQuestion.correct_answer;
            const isSelected = selectedOption === option.key;

            const baseStyles = 'w-full p-4 border-2 rounded-lg text-left transition-all duration-200';
            let stateStyles = 'border-gray-300 dark:border-gray-600';

            if (!isSubmitted) {
              stateStyles = isSelected
                ? 'border-subject-500 bg-subject-50 dark:bg-subject-900/20'
                : 'border-gray-300 dark:border-gray-600 hover:border-subject-300';
            } else if (attemptResult) {
              if (isCorrect) {
                stateStyles = 'border-green-500 bg-green-50 dark:bg-green-900/20';
              } else if (isSelected) {
                stateStyles = 'border-red-500 bg-red-50 dark:bg-red-900/20';
              }
            }

            return (
              <button
                key={option.key}
                onClick={() => handleSelect(option.key)}
                disabled={isSubmitted || isSubmitting || !timerActive}
                className={`${baseStyles} ${stateStyles} ${
                  !isSubmitted && !isSubmitting ? 'hover:shadow-sm cursor-pointer' : 'cursor-default'
                }`}
              >
                <div className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-sm font-medium">
                    {option.key}
                  </span>
                  <div className="flex-1 text-gray-900 dark:text-white">
                    <MathRenderer latex={option.text} />
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {revealed && !isSubmitted && (
        <div className="flex justify-end mt-4">
          <button
            onClick={handleSubmit}
            disabled={!selectedOption || isSubmitting || !timerActive}
            className="px-5 py-2 bg-subject-600 text-white rounded-lg hover:bg-subject-500 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Answer'}
          </button>
        </div>
      )}

      {errorMessage && (
        <div className="mt-4 px-4 py-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-200">
          {errorMessage}
        </div>
      )}

      {attemptResult && (
        <div className="mt-4 p-4 rounded-lg bg-gray-50 dark:bg-gray-700 space-y-3">
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              Correct Answer: {subQuestion.correct_answer}
            </span>
            <span
              className={`text-sm font-semibold ${
                attemptResult.is_correct ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
              }`}
            >
              {attemptResult.is_correct ? '✓ Correct!' : '✗ Incorrect'}
            </span>
          </div>

          <div>
            <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Area of Study
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-300">{areaOfStudy}</div>
          </div>

          {tags && tags.length > 0 && (
            <div>
              <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Tags
              </div>
              <div className="flex flex-wrap gap-2">
                {tags.map((tag) => (
                  <span
                    key={tag.id}
                    className="px-2 py-1 text-xs font-medium rounded-full bg-subject-100 text-subject-800 dark:bg-subject-900/30 dark:text-subject-200"
                  >
                    {tag.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          <RatingChange result={attemptResult} />

          {subQuestion.worked_solution ? (
            <WorkedSolutionPanel solution={subQuestion.worked_solution} isVisible />
          ) : (
            <div className="px-4 py-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg text-sm text-yellow-800 dark:text-yellow-200">
              Worked solution not available yet.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ShortAnswerQuestionCard: React.FC<ShortAnswerQuestionCardProps> = ({
  question,
  onSubmitAnswer,
  onNextQuestion,
  practiceLimitReached = false,
  tags,
  showSimilarQuestion = false,
  canGenerateSimilar = false,
  showTimer = false,
  onBookmarkChange,
}) => {
  const [aggregatedProgress, setAggregatedProgress] = useState<QuestionProgress | null | undefined>(
    question.progress
  );
  const [subQuestionProgress, setSubQuestionProgress] = useState<Record<string, QuestionProgress | null | undefined>>(
    () =>
      Object.fromEntries(
        question.sub_questions.map((sub) => [sub.id, sub.progress])
      )
  );
  const [completedIds, setCompletedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    setAggregatedProgress(question.progress);
    setSubQuestionProgress(
      Object.fromEntries(question.sub_questions.map((sub) => [sub.id, sub.progress]))
    );
  }, [question.progress, question.sub_questions]);

  useEffect(() => {
    setCompletedIds(new Set());
  }, [question.id]);

  const statusPill = useMemo(() => getProgressStatus(aggregatedProgress), [aggregatedProgress]);
  const allCompleted = completedIds.size === question.sub_questions.length;
  const questionTypeLabel = question.question_type === 'ER' ? 'Extended Response' : 'Short Answer';
  const questionTypeBadgeClass =
    question.question_type === 'ER'
      ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-200'
      : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-200';
  const similarQuestions = useMemo(
    () =>
      question.sub_questions.map((subQuestion, index) => {
        const label =
          subQuestion.sub_question === 'N/A'
            ? `Part ${String.fromCharCode(65 + index)}`
            : `Part ${subQuestion.sub_question}`;
        return { id: subQuestion.id, label };
      }),
    [question.sub_questions]
  );

  const handleAttemptComplete = useCallback(
    (subQuestion: ShortAnswerSubQuestion, result: AttemptResult) => {
      setCompletedIds((prev) => {
        const next = new Set(prev);
        next.add(subQuestion.id);
        return next;
      });

      if (result.question_progress) {
        setAggregatedProgress(result.question_progress);
        setSubQuestionProgress((prev) => ({
          ...prev,
          [subQuestion.id]: result.question_progress,
        }));
      }
    },
    []
  );

  const handleSubmit = useCallback(
    async (
      subQuestion: ShortAnswerSubQuestion,
      selectedAnswer: string,
      autoSubmitted?: boolean,
      timeSpentSeconds?: number
    ) => {
      if (practiceLimitReached) {
        throw new Error('Practice limit reached');
      }
      return onSubmitAnswer({
        questionId: subQuestion.id,
        selectedAnswer,
        autoSubmitted,
        timeSpentSeconds,
      });
    },
    [onSubmitAnswer, practiceLimitReached]
  );

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 max-w-4xl mx-auto space-y-5">
      <div className="flex justify-between items-start mb-4 gap-4">
        <div>
          <div className="text-sm text-gray-500 dark:text-gray-400 mb-2">
            {question.year} Exam {question.exam_type} · Q{question.question_number}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${questionTypeBadgeClass}`}>
              {questionTypeLabel}
            </span>
            <span className="text-sm text-gray-500 dark:text-gray-400">Difficulty:</span>
            <span className={`text-sm font-medium ${getDifficultyColor(question.difficulty_rating)}`}>
              {question.difficulty_rating === null || question.difficulty_rating === undefined
                ? 'Unknown'
                : `${getDifficultyLabel(question.difficulty_rating)} (${question.difficulty_rating})`}
            </span>
            {question.total_marks ? (
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Total Marks: {question.total_marks}
              </span>
            ) : null}
          </div>
        </div>
        <span className={`px-3 py-1 text-xs font-semibold rounded-full whitespace-nowrap ${statusPill.className}`}>
          {statusPill.label}
        </span>
      </div>

      <div className="space-y-5">
        {question.sub_questions.map((subQuestion, index) => {
          const previousId = index > 0 ? question.sub_questions[index - 1].id : null;
          const isTimerEnabled = showTimer && (index === 0 || (previousId ? completedIds.has(previousId) : true));
          return (
          <SubQuestionPanel
            key={subQuestion.id}
            subQuestion={subQuestion}
            tags={tags ?? question.tags}
            areaOfStudy={question.vce_area_of_study}
            progress={subQuestionProgress[subQuestion.id]}
            disabled={practiceLimitReached}
            onSubmit={(selectedAnswer, auto, timeSpentSeconds) =>
              handleSubmit(subQuestion, selectedAnswer, auto, timeSpentSeconds)
            }
            onAttemptComplete={(result) => handleAttemptComplete(subQuestion, result)}
            showTimer={showTimer}
            timerEnabled={isTimerEnabled}
            autoStartTimer={index === 0}
            onBookmarkChange={onBookmarkChange}
          />
          );
        })}
      </div>

      {practiceLimitReached && (
        <div className="px-4 py-3 bg-subject-50 dark:bg-subject-900/20 border border-subject-200 dark:border-subject-800/40 rounded-lg text-sm text-subject-800 dark:text-subject-200">
          Daily practice limit reached. Upgrade your plan or check back tomorrow.
        </div>
      )}

      {onNextQuestion && allCompleted && (
        <div className="mt-4 flex justify-center">
          <button
            onClick={onNextQuestion}
            className="px-6 py-2 bg-subject-600 text-white rounded-lg hover:bg-subject-500 transition-colors font-medium"
          >
            Next Question
          </button>
        </div>
      )}

      {showSimilarQuestion && similarQuestions.length > 0 && (
        <SimilarQuestionPanel
          questions={similarQuestions}
          canGenerate={canGenerateSimilar}
        />
      )}
    </div>
  );
};

export default ShortAnswerQuestionCard;
